import { useState } from 'react'
import {
  IconButton,
  Popover,
  MenuList,
  MenuItem,
  Typography,
} from '@mui/material'
import MoreHorizIcon from '@mui/icons-material/MoreHoriz'
import EditIcon from '@/assets/crm/EditIcon.svg'
import DeleteIcon from '@/assets/crm/DeletIcon.svg'
import SenderIcon from '@/assets/crm/SenderIcon.svg'
import { useCRMStore } from '@/store/crm/crmStore'
import { useAppTheme } from '@/theme/ThemeContext'
import ConfirmDeleteDialog from '@/components/Analytics/ConfirmDeleteDialog'
import {
  deleteCrmTask,
  getCrmErrorMessage,
  sendCrmTaskReminder,
} from '@/services/crmService'
import { useCrmAccountId, useRefreshCrmTasks } from '@/hooks/useCrm'

type RowActionsProps = {
  taskId: number
}
const RowActions: React.FC<RowActionsProps> = ({ taskId }) => {
  const { setCRMTaskCreatorModal, showToast } = useCRMStore()
  const accountId = useCrmAccountId()
  const refreshTasks = useRefreshCrmTasks(accountId)
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
  const [confirmOpen, setConfirmOpen] = useState(false)
  const [busy, setBusy] = useState(false)
  const { mode } = useAppTheme()
  const isDarkMode = mode === 'dark'

  const BG_COLOR = isDarkMode ? '#202B40' : '#FFF'
  const TEXT_COLOR = isDarkMode ? '#FFFFFF' : '#003F61'
  const HOVER_BG = isDarkMode ? '#2d4a7c' : '#E3F5FF'

  const handleOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget)
  }

  const handleClose = () => {
    setAnchorEl(null)
  }

  const handleEdit = () => {
    handleClose()
    setCRMTaskCreatorModal({
      mode: 'edit',
      open: true,
      data: { taskId },
    })
  }

  const handleReminder = async () => {
    handleClose()
    if (!accountId || busy) return
    setBusy(true)
    try {
      await sendCrmTaskReminder(accountId, taskId)
      showToast('התזכורת נשלחה למשויך')
    } catch (error) {
      showToast(await getCrmErrorMessage(error, 'שליחת התזכורת נכשלה'), 'error')
    } finally {
      setBusy(false)
    }
  }

  const handleDelete = async () => {
    setConfirmOpen(false)
    if (!accountId || busy) return
    setBusy(true)
    try {
      await deleteCrmTask(accountId, taskId)
      showToast('המשימה נמחקה')
      await refreshTasks()
    } catch (error) {
      showToast(await getCrmErrorMessage(error, 'מחיקת המשימה נכשלה'), 'error')
    } finally {
      setBusy(false)
    }
  }

  const open = Boolean(anchorEl)

  return (
    <>
      <IconButton onClick={handleOpen} disabled={busy}>
        <MoreHorizIcon sx={{ color: isDarkMode ? '#FFFFFF' : 'inherit' }} />
      </IconButton>

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        sx={{
          '& .MuiPaper-root': {
            backgroundColor: BG_COLOR,
            backgroundImage: 'none',
          },
        }}
      >
        <MenuList
          sx={{
            width: 'fit-content',
            padding: '8px 0',
            background: BG_COLOR,
            boxShadow: '0 3px 11.9px -1px rgba(29, 57, 129, 0.04)',
            borderRadius: '4px',
          }}
        >
          <MenuItem
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 1,
              '&:hover': {
                backgroundColor: HOVER_BG,
              },
            }}
            onClick={handleEdit}
          >
            <Typography
              sx={{
                fontSize: '14px',
                fontWeight: '400',
                fontStyle: 'normal',
                color: TEXT_COLOR,
                fontFamily: 'Rubik, sans-serif',
              }}
            >
              פרטים ועריכה
            </Typography>
            <img
              src={EditIcon}
              alt="Edit"
              style={{
                filter: isDarkMode ? 'brightness(0) invert(1)' : 'none',
              }}
            />
          </MenuItem>
          <MenuItem
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 1,
              '&:hover': {
                backgroundColor: HOVER_BG,
              },
            }}
            onClick={handleReminder}
          >
            <Typography
              sx={{
                fontSize: '14px',
                fontWeight: '400',
                fontStyle: 'normal',
                color: TEXT_COLOR,
                fontFamily: 'Rubik, sans-serif',
              }}
            >
              שליחת תזכורת למשויך
            </Typography>
            <img
              src={SenderIcon}
              alt="Sender"
              style={{
                filter: isDarkMode ? 'brightness(0) invert(1)' : 'none',
              }}
            />
          </MenuItem>
          <MenuItem
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 1,
              '&:hover': {
                backgroundColor: HOVER_BG,
              },
            }}
            onClick={() => {
              handleClose()
              setConfirmOpen(true)
            }}
          >
            <Typography
              sx={{
                fontSize: '14px',
                fontWeight: '400',
                fontStyle: 'normal',
                color: '#E53935',
                fontFamily: 'Rubik, sans-serif',
              }}
            >
              מחיקה
            </Typography>
            <img src={DeleteIcon} alt="Delete" />
          </MenuItem>
        </MenuList>
      </Popover>
      <ConfirmDeleteDialog
        open={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        onConfirm={handleDelete}
        title="מחיקת משימה"
        content="האם למחוק את המשימה?"
      />
    </>
  )
}

export default RowActions
